import * as angular from 'angular';
import * as _ from 'lodash';

import { jobStarted, tasksChanged } from '../../../common/';
import { ITaskService, ILoggerService, ICurrentApplicationService, ISignalrService } from '../../../services';
import { ITaskUpdate, ITask, ComponentDefinition, TaskStatus, ITaskConfiguration, WorkflowTaskConfiguration, Group } from '../../../models';

interface ITaskConfigurationVM extends ITaskConfiguration {
    isActionWaiting?: boolean;
    groupName?: string;
    groupCount?: number;
    count?: number;
    dateCollection?: any[];
    groupGuidList?: string[];
}

class TasksListController implements angular.IComponentController {
    editedTask: ITaskConfiguration;
    groups: { [guid: string]: Group } = {};
    isLoading: boolean;
    loadingMessage: string;
    tasks: ITask[];

    private _proxy: SignalR.Hub.Proxy;
    private _watchers: (() => void)[];

    static $inject = [
        '$rootScope',
        '$scope',
        '$q',
        'taskService',
        'loggerService',
        'currentApplicationService',
        'signalrService'
    ];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _taskService: ITaskService,
        private _loggerService: ILoggerService,
        private _currentApplicationService: ICurrentApplicationService,
        private _signalrService: ISignalrService
    ) {
        this.liveRefresh = this.liveRefresh.bind(this);
    }

    $onInit() {
        this.isLoading = true;
        this.loadingMessage = 'Loading groups and tasks';
        this._$q
            .all([
                this._taskService.refresh()
            ])
            .then(data => {
                this.refresh();
            })
            .catch(reason => {
                this._loggerService.logError('Cannot load tasks');
            })
            .finally(() => this.isLoading = false);

        this._$scope.$on(tasksChanged, () => this.refresh());
        this._$scope.$on(jobStarted, () => this.refresh());
        this._watchers = [
            this._$rootScope.$on('$stateChangeStart', () => {
                if (this._proxy) {
                    this._proxy.off('push', this.liveRefresh);
                }
            })
        ];

        this.initializeSignalr();
    }

    $onDestroy() {
        try {
            if (this._watchers && this._watchers.length) {
                this._watchers.forEach(w => w());
            }
        } catch (e) {
            console.error(e);
        }
    }

    start(task: ITaskConfigurationVM): void {
        this.editedTask = angular.copy(task);
    }

    resume(task: ITaskConfigurationVM): void {
        task.isActionWaiting = true;
        this._taskService
            .resume(task.guid)
            .then(() => {
                this.refresh();
                this._loggerService.logSuccess('Task restarted.');
            })
            .catch(() => this._loggerService.logError('Failed to restart task.'))
            .finally(() => task.isActionWaiting = false);
    }

    cancel(task: ITaskConfigurationVM): void {
        task.isActionWaiting = true;
        this._taskService
            .cancel(task.guid)
            .then(() => {
                this.refresh();
                this._loggerService.logSuccess('Task cancelled.');
            })
            .catch(() => this._loggerService.logError('Failed to cancel task.'))
            .finally(() => task.isActionWaiting = false);
    }

    private updateTask(guid: string, progress: number, status: number, resultsStatus: number | null = null) {
        const found = _.find(this.tasks, t => t.guid === guid);
        if (found) {
            found.progress = progress;
            found.status = status;
            found.resultsStatus = resultsStatus;
            this._$scope.$digest();
            this._$scope.$apply();
        } else {
            console.warn(`Task ${guid} not found.`);
        }
    }

    private initializeSignalr(): void {
        const proxy = this._signalrService.connection.createHubProxy('taskHub');
        this._signalrService.connect(() => {
            const app = this._currentApplicationService.getApplicationConfiguration();
            proxy.on('push', this.liveRefresh);
            this._$scope.$on('$destroy', () => this._proxy.off('push', this.liveRefresh));
            this._proxy = proxy;
            proxy.invoke('joinWorkEnvironment', app.guid);
        });
    }

    private liveRefresh(report: ITaskUpdate) {
        this.updateTask(report.TaskGuid, report.Progress, report.Status);

    }

    private refresh() {
        this.isLoading = true;
        return this._taskService
            .getAll(true)
            .then(data => {
                data.forEach((d: ITaskConfigurationVM) => {
                    this.addDescriptionParams(d);
                    // override for workflow tasks
                    // seems bad as we hardcode a special case for a special type
                    // should be in descriptionParams
                    const groupGuid = (d as WorkflowTaskConfiguration).groupGuid;
                    if (groupGuid && this.groups) {
                        d.groupName = this.groups[groupGuid]?.uniqueName ?? '';
                        d.count = d.dateCollection?.length ?? 0;
                        d.groupCount = d.groupGuidList?.length ?? 0;
                    }
                    d.isActionWaiting = false;
                });
                this.tasks = data as unknown as ITask[];
                this.isLoading = false;
            })
            .catch(() => {
                this.isLoading = false;
            });
    }

    private addDescriptionParams(d: ITaskConfigurationVM) {
        try {
            if (d.descriptionParams) {
                const params = JSON.parse(d.descriptionParams);
                Object.keys(params).forEach(k => d[k] = params[k]);
            }
        } catch (e) {
            // do nothing
        }
            
    }

    getClass(task: ITask) {
        let result = 'progress-bar-success';
        if (task.status === TaskStatus.Failed) {
            result = 'progress-bar-danger';
        } else {
            result = task.resultsStatus === null ||
                task.resultsStatus === undefined || task.resultsStatus === TaskStatus.Created
                ? 'progress-bar-success'
                : task.resultsStatus === TaskStatus.Started
                    ? 'progress-bar-warning'
                    : 'progress-bar-danger';
        }
        return result;
    }

    checkDisplay(button: string, task: ITask): boolean {
        let isDisplayed: boolean = false;
        switch (button) {
            case 'cancel':
                isDisplayed = task.status === TaskStatus.Started
                    || task.status === TaskStatus.Created
                    || task.status === TaskStatus.Cancelling;
                break;
            case 'continue':
                isDisplayed = task.status === TaskStatus.Cancelled;
                break;
            case 'restart':
                isDisplayed = true;
                break;
        }
        return isDisplayed;
    }
}

export const wtTasksList: ComponentDefinition = {
    componentId: 'wtTasksList',
    controller: TasksListController,
    template: require('./tasksList.component.html'),
    controllerAs: 'vm',
    bindings: {
        groups: '<'
    }
};